// Production-only Google Analytics 4 for RAQOZ public marketing and authentication pages.
// Consent is owned by src/lib/analytics.ts, which is the single gtag.js loader.
// No user, organisation, supplier, quotation, financial or contact data is ever sent.
import { analyticsConsent, CONSENT_EVENT, ensureDestination, sendMeasurementEvent } from "@/lib/analytics";

const isProduction = import.meta.env.PROD || import.meta.env.VITE_RAQOZ_ENV === "production";

let lastPagePath = "";
let consentListenerBound = false;

export function ga4Trackable(path: string): boolean {
  const clean = path.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  return clean !== "/app" && !clean.startsWith("/app/");
}

function cleanPath(path: string): string {
  const value = path.split("?")[0].split("#")[0];
  return value.startsWith("/") ? value : `/${value}`;
}

function measurementAllowed(): boolean {
  return isProduction && typeof window !== "undefined" && analyticsConsent() === "granted";
}

export function initGa4(): boolean {
  if (!measurementAllowed()) return false;
  return ensureDestination();
}

export function ga4PageView(path: string) {
  bindConsentListener();
  if (!measurementAllowed()) return;
  const pagePath = cleanPath(path);
  if (!ga4Trackable(pagePath) || pagePath === lastPagePath) return;
  if (!initGa4()) return;
  lastPagePath = pagePath;
  sendMeasurementEvent("page_view", {
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}`,
  });
}

function bindConsentListener() {
  if (consentListenerBound || typeof window === "undefined") return;
  consentListenerBound = true;
  window.addEventListener(CONSENT_EVENT, () => {
    if (analyticsConsent() !== "granted") return;
    ga4PageView(window.location.pathname);
  });
}

function ga4Event(name: string, ctaLabel: string) {
  if (!measurementAllowed()) return;
  const pagePath = cleanPath(window.location.pathname);
  if (!ga4Trackable(pagePath)) return;
  if (!initGa4()) return;
  sendMeasurementEvent(name, {
    cta_label: ctaLabel.slice(0, 60),
    page_path: pagePath,
  });
}

export function ga4SignUpStart(ctaLabel: string) { ga4Event("sign_up_start", ctaLabel); }
export function ga4GenerateLead(ctaLabel: string) { ga4Event("generate_lead", ctaLabel); }
